import React from 'react';
import { useTheme } from '../context/ThemeContext';
import { Background3D } from '../components/Background3D';
import { ArrowRight } from 'lucide-react';

export function Research() {
  const { theme } = useTheme();
  
  const researchAreas = [
    {
      title: "Education Technology",
      description: "Developing innovative educational platforms and AI-powered learning systems to accelerate human potential.",
      image: "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?auto=format&fit=crop&q=80"
    },
    {
      title: "Artificial Intelligence",
      description: "Advancing the frontiers of AI through novel architectures, training methodologies, and reasoning capabilities.",
      image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&q=80"
    },
    {
      title: "Natural Language Processing",
      description: "Building systems that can understand, generate, and reason with human language at unprecedented levels.",
      image: "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?auto=format&fit=crop&q=80"
    }
  ];

  const publications = [
    {
      title: "Daedalus: Code Reasoning Models",
      journal: "ICML",
      year: "2024",
      impact: "Advanced code generation and debugging"
    },
    {
      title: "Apollo: Instruction-Following Model Family",
      journal: "NeurIPS",
      year: "2024",
      impact: "Consistent multi-scale reasoning systems"
    },
    {
      title: "Victor Academy: AI-Enhanced Education",
      journal: "Educational Technology Research",
      year: "2024",
      impact: "Human acceleration through personalized learning"
    }
  ];
  
  return (
    <>
      <Background3D />
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 overflow-hidden">
          <div className="max-w-[1440px] mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h1 className="text-6xl font-normal mb-8 leading-tight">
                  Advancing the frontiers of <span className="text-gradient">artificial intelligence</span>
                </h1>
                <p className={`text-xl leading-relaxed mb-8 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Our research pushes the boundaries of what's possible in AI, 
                  focusing on developing safe and ethical systems that benefit humanity.
                </p>
                <div className="flex items-center space-x-6">
                  <button className="group inline-flex items-center px-6 py-3 text-lg text-white bg-primary-600 rounded-full hover:bg-primary-700 transition-all duration-300 shadow-soft hover:shadow-lg">
                    View Publications
                    <ArrowRight className="ml-2 w-5 h-5 transform group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-radial from-primary-500/20 to-transparent opacity-70"></div>
                <img
                  src="https://images.unsplash.com/photo-1620712943543-bcc4688e7485?auto=format&fit=crop&q=80"
                  alt="AI Research"
                  className="w-full h-[500px] object-cover rounded-3xl shadow-2xl hover-card"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Research Areas */}
        <section className={`py-32 ${theme === 'dark' ? 'bg-dark-200/50' : 'bg-white/50'}`}>
          <div className="max-w-[1440px] mx-auto px-6">
            <h2 className="text-4xl font-normal mb-16">Research Areas</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {researchAreas.map((area, index) => (
                <div
                  key={index}
                  className={`group rounded-2xl overflow-hidden hover-card ${
                    theme === 'dark' ? 'bg-dark-300' : 'bg-white'
                  }`}
                >
                  <div className="relative h-48 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <img
                      src={area.image}
                      alt={area.title}
                      className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-2xl font-normal mb-4 group-hover:text-primary-500 transition-colors duration-200">
                      {area.title}
                    </h3>
                    <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                      {area.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Publications */}
        <section className="py-32">
          <div className="max-w-[1440px] mx-auto px-6">
            <h2 className="text-4xl font-normal mb-16">Latest Publications</h2>
            <div className="grid gap-8">
              {publications.map((pub, index) => (
                <div
                  key={index}
                  className={`p-8 rounded-2xl hover-card ${
                    theme === 'dark' ? 'bg-dark-300' : 'bg-white'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-2xl font-normal mb-2">{pub.title}</h3>
                      <p className={`mb-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        {pub.journal} • {pub.year}
                      </p>
                      <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
                        {pub.impact}
                      </p>
                    </div>
                    <button className="group inline-flex items-center text-primary-600 hover:text-primary-700">
                      Read Paper
                      <ArrowRight className="ml-2 w-5 h-5 transform group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}